<template>
  <v-card elevation="0" class="mt-4">
    <v-card-text class="pb-2">
      <h4>{{ $t('routes.labels.categories') }} ({{ categoryCount }})</h4>
    </v-card-text>
    <v-card-text v-if="categories.length > 0" class="pt-0">
      <v-treeview
        :items="categories"
        open-all hoverable
        activatable
        density="compact"
        return-object
        item-key="id"
        @update:activated="selectActiveCategory"
      >
        <template v-slot:append="{ item }">
          <div class="category-item d-flex align-center">
            <v-spacer></v-spacer>
            <CreateUpdateCategoryDialog :category="item" @categoryUpdated="refreshCategories">
              <v-btn
                rounded="circle"
                width="36px"
                height="36px"
                variant="text"
                icon="mdi-pencil"
                class="mr-2"
                elevation="0"
                data-cy="tenantLabels-options-createUpdateCategory"
              />
            </CreateUpdateCategoryDialog>
            <DeleteCategoryDialog :category="item" @categoryDeleted="processCategoryDeletion">
              <v-btn
                rounded="circle"
                width="36px"
                height="36px"
                variant="text"
                icon="mdi-delete"
                class="mr-2"
                elevation="0"
                data-cy="tenantLabels-options-createUpdateCategory"
              />
            </DeleteCategoryDialog>
          </div>
        </template>

        <template v-slot:title="{ item }">
          {{ item.name }}
        </template>
      </v-treeview>
    </v-card-text>
    <v-card-text v-else>
      <div class="font-italic">
        {{ $t('routes.labels.noCategories') }}
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useLabelStore } from '@/stores/LabelStore.js'
import CreateUpdateCategoryDialog from '@/routes/console/labels/components/dialogs/CreateUpdateCategoryDialog.vue';
import DeleteCategoryDialog from '@/routes/console/labels/components/dialogs/DeleteCategoryDialog.vue';

export default {
  name: 'CategoryListView',
  components: {
    CreateUpdateCategoryDialog,
    DeleteCategoryDialog,
  },
  data() {
    return {
      flattenedCategories: [],
    };
  },
  watch: {
    categories: function(newVal) {
      this.flattenedCategories = this.flatCategories(newVal);
    },
  },
  mounted() {
    this.refreshCategories();
  },
  methods: {
    selectActiveCategory(activeItems) {
      const selectedCategory = activeItems.length ? activeItems[0] : null;
      if (selectedCategory) {
        const labelStore = useLabelStore();
        labelStore.setActiveCategoryId(selectedCategory.id);

        this.refreshLabels(selectedCategory.id);
      }
    },
    countCategories(categories) {
      let count = 0;

      function countRecursively(categories) {
        for (const category of categories) {
          count += 1;
          if (category.children) {
            countRecursively(category.children);
          }
        }
      }

      countRecursively(categories);
      return count;
    },
    refreshLabels(categoryId) {
      const payload = {
        categoryId: categoryId,
        accessPointId: this.accessPointId,
      };

      const labelStore = useLabelStore();
      labelStore.refreshLabels(payload);
    },
    refreshCategories: function() {
      const payload = {
        accessPointId: this.accessPointId,
      };

      const labelStore = useLabelStore();
      labelStore.refreshCategories(payload)
        .catch((error) => {
          console.error('Error fetching data', error);
        });
    },
    setActiveCategoryId(categoryId) {
      const labelStore = useLabelStore();
      labelStore.setActiveCategoryId(categoryId)
    },
    processCategoryDeletion: function(payload) {
      const deletedCategoryId = payload.categoryId;
      const activeCategoryId = this.activeCategoryId;
      const isAncestor = this.isAncestor(deletedCategoryId, activeCategoryId);

      // Check if deleted category is the active one OR an ancestor
      if (deletedCategoryId === activeCategoryId || isAncestor) {
        this.setActiveCategoryId(null);
      }

      this.refreshCategories();
    },

    // Helper function to check ancestor relationship
    isAncestor(ancestorId, childId) {
      let currentCategory = this.getCategoryById(childId);

      // Traverse up the category tree
      while (currentCategory) {
        if (currentCategory.parentid === ancestorId) {
          return true; // Found ancestor
        }

        currentCategory = this.getCategoryById(currentCategory.parentid);
      }

      return false; // Ancestor not found
    },

    getCategoryById(categoryId) {
      const foundCategory = this.flattenedCategories.find((category) => category.id === categoryId);
      return foundCategory;
    },
    flatCategories(categoryCollection) {
      const flattenCategories = (categories, level = 0) => {
        return categories.reduce((acc, category) => {
          let name = category.name;

          if (level > 0) {
            name = '└' + '─'.repeat(level * 2) + ' ' + category.name;
          }

          acc.push({
            ...category,
            name,
            level,
          });

          if (category.children) {
            acc = acc.concat(flattenCategories(category.children, level + 1));
          }

          return acc;
        }, []);
      };

      return flattenCategories(categoryCollection, 0);
    },
  },
  computed: {
    categories() {
      const labelStore = useLabelStore();
      return labelStore.getCategories;
    },
    categoryCount() {
      return this.countCategories(this.categories);
    },
    accessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    activeCategoryId: function() {
      const labelStore = useLabelStore();
      return labelStore.getActiveCategoryId;
    },
  },
};
</script>

<style scoped>
.category-item {
  cursor: pointer;
  padding-left: 16px;
}
</style>
